/**
 * Normative runtime constants from SPEC-signaling-v1.md v1.6, especially
 * section 10 plus values cited by the referenced normative prose and the
 * whiteboard constants folded from SPEC-whiteboard-v1.md v1.0.
 *
 * Durations are milliseconds, sizes are bytes, and bitrates are bits per second.
 * The final LEG_UNREACHABLE_INTERVALS value repeats for all later attempts.
 */
export const PROTOCOL_CONSTANTS = {
    PROTO_VERSION_MIN: 1,
    PROTO_VERSION_MAX: 1,
    SIGNAL_PORT: 8_443,
    T_HB_INTERVAL: 5_000,
    T_HB_TIMEOUT: 15_000,
    // Normative in §8.1 and the §10 heartbeat note for backgrounded browser tabs.
    T_HIDDEN_PONG_TOLERANCE: 90_000,
    T_CONNECT: 8_000,
    T_RELAY_WARMUP: 3_000,
    T_DISC_GRACE: 5_000,
    T_RESTART_WINDOW: 60_000,
    // Normative in section 6.2; the section 10 table omits dedicated rows.
    LEG_RESTART_MAX: 2,
    T_RESTART_STABLE_RESET: 30_000,
    T_RESUME: 300_000,
    T_SOURCE_GRACE: 30_000,
    T_SHARE_READY: 60_000,
    T_MODERATION: 30_000,
    T_HOST_RECOVER: 10_000,
    T_CLOSED_RETAIN: 60_000,
    CLOSED_HISTORY_MAX: 256,
    WS_BACKOFF_BASE: 500,
    WS_BACKOFF_CAP: 5_000,
    LEG_BACKOFF_BASE: 1_000,
    LEG_BACKOFF_CAP: 8_000,
    LEG_BACKOFF_MAX: 3,
    LEG_UNREACHABLE_INTERVALS: [8_000, 8_000, 8_000, 30_000, 60_000],
    RELAY_MAX_HEIGHT: 720,
    RELAY_MAX_FPS: 15,
    RELAY_MAX_BITRATE: 1_500_000,
    RELAY_DEGRADED_HEIGHT: 360,
    RELAY_DEGRADED_FPS: 10,
    RELAY_CPU_BUDGET_PERCENT: 60,
    MAX_MSG_SIZE: 64 * 1_024,
    LIMIT_MSGS: {
        ratePerSecond: 30,
        burst: 60,
    },
    LIMIT_SYNC_REQUEST: {
        count: 1,
        windowMs: 2_000,
    },
    LIMIT_PREJOIN_SOCKETS: {
        perIp: 3,
        total: 20,
        idleTimeoutMs: 10_000,
    },
    // Diagnostics authentication keeps its already-ratified independent abuse window.
    LIMIT_DIAGNOSTICS_AUTH: {
        perIpPerMinute: 20,
        windowMs: 60_000,
        lockoutMs: 60_000,
    },
    TICKET_TTL: 300_000,
    WB_DOC_SOFT_CAP: 8 * 1_024 * 1_024,
    WB_DOC_HARD_CAP: 16 * 1_024 * 1_024,
    WB_MAX_PTS_PER_STROKE: 256,
    WB_TEXT_MAX_CP: 1_000,
    WB_AWARENESS_MIN_INTERVAL: 50,
    WB_AWARENESS_MAX_BYTES: 1_024,
    WB_UNDO_CAPTURE_TIMEOUT: 0,
    WB_DIAG_SAMPLE_INTERVAL: 10_000,
};
/** Host-initiated WebSocket close codes from SPEC section 9. */
export const CLOSE_CODES = {
    MESSAGE_TOO_BIG: 1009,
    PROTOCOL_ERROR: 4000,
    VERSION_MISMATCH: 4001,
    ROOM_FULL: 4002,
    SUPERSEDED: 4003,
    ROOM_CLOSED: 4004,
    ADMIN_KICK: 4005,
    INTERNAL: 4006,
    AUTH_FAILURE: 4007,
};
/**
 * Error-code registry from SPEC section 9.
 *
 * `fatal` is the normal wire default. `INVALID_PAYLOAD` is made fatal for a
 * malformed join and `INTERNAL` may be made fatal by the boundary that caught
 * the error.
 */
export const ERROR_CODES = {
    VERSION_MISMATCH: {
        code: "VERSION_MISMATCH",
        fatal: true,
        closeCode: CLOSE_CODES.VERSION_MISMATCH,
    },
    ROOM_FULL: {
        code: "ROOM_FULL",
        fatal: true,
        closeCode: CLOSE_CODES.ROOM_FULL,
    },
    ROOM_CLOSED: {
        code: "ROOM_CLOSED",
        fatal: true,
        closeCode: CLOSE_CODES.ROOM_CLOSED,
    },
    RESUME_UNAUTHORIZED: {
        code: "RESUME_UNAUTHORIZED",
        fatal: true,
        closeCode: CLOSE_CODES.AUTH_FAILURE,
    },
    LICENSE_EXPIRED: {
        code: "LICENSE_EXPIRED",
        fatal: true,
        closeCode: CLOSE_CODES.AUTH_FAILURE,
    },
    LICENSE_INVALID: {
        code: "LICENSE_INVALID",
        fatal: true,
        closeCode: CLOSE_CODES.AUTH_FAILURE,
    },
    SESSION_STATEMENT_INVALID: {
        code: "SESSION_STATEMENT_INVALID",
        fatal: false,
    },
    INVALID_PAYLOAD: {
        code: "INVALID_PAYLOAD",
        fatal: false,
        closeCode: CLOSE_CODES.PROTOCOL_ERROR,
    },
    UNSUPPORTED_TYPE: {
        code: "UNSUPPORTED_TYPE",
        fatal: false,
    },
    NOT_SOURCE: {
        code: "NOT_SOURCE",
        fatal: false,
    },
    SHARE_DENIED: {
        code: "SHARE_DENIED",
        fatal: false,
    },
    STALE_SHARE: {
        code: "STALE_SHARE",
        fatal: false,
    },
    SOURCE_UNREACHABLE: {
        code: "SOURCE_UNREACHABLE",
        fatal: false,
    },
    RATE_LIMITED: {
        code: "RATE_LIMITED",
        fatal: false,
        closeCode: CLOSE_CODES.PROTOCOL_ERROR,
    },
    INTERNAL: {
        code: "INTERNAL",
        fatal: false,
        closeCode: CLOSE_CODES.INTERNAL,
    },
};
/** Exact W10 v1 framing constants from SPEC-data-channel-v1 section 5. */
export const W10_DATA_CHANNEL_V1 = Object.freeze({
    magic: "KSDC",
    controlLabel: "klass-share-control",
    whiteboardLabel: "klass-share-whiteboard",
    protocol: "klass-share-data",
    wireVersion: 1,
    headerByteLength: 20,
    maxSendByteLength: 16_384,
    maxChunkPayloadByteLength: 16_364,
    maxControlMessageByteLength: 65_536,
    maxWhiteboardMessageByteLength: 16_777_222,
});
export const W10_DATA_CHANNEL_KINDS = Object.freeze({
    hello: 0x01,
    ready: 0x02,
    reject: 0x03,
    control: 0x10,
    whiteboard: 0x20,
});
export const W10_DATA_CHANNEL_BACKPRESSURE_V1 = Object.freeze({
    control: Object.freeze({
        lowByteLength: 16 * 1_024,
        highByteLength: 64 * 1_024,
        pendingByteLength: 256 * 1_024,
    }),
    whiteboard: Object.freeze({
        lowByteLength: 256 * 1_024,
        highByteLength: 1 * 1_024 * 1_024,
    }),
    drainTimeoutMs: 10_000,
});
export const W10_DATA_CHANNEL_REASSEMBLY_V1 = Object.freeze({
    inactivityTimeoutMs: 20_000,
    absoluteTimeoutMs: 120_000,
});
//# sourceMappingURL=constants.js.map